import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@cr-environments/environment';
import { catchError, map, Observable, throwError } from 'rxjs';
import {
  ChargePointKind,
  ChatResponse,
  CognigyBotPayload,
  CognigyChatRequest,
  CognigyResponse,
  WattsonPricePolicy,
} from './ai.models';

const FALLBACK_ANSWER = 'Entschuldigung, ich habe mich verschluckt. Bitte frag nochmal.';

@Injectable({ providedIn: 'root' })
export class AIAPIService {
  private readonly http = inject(HttpClient);

  private sessionId = crypto.randomUUID();

  public resetSession(): void {
    this.sessionId = crypto.randomUUID();
  }

  public sendQuestion(
    question: string,
    userId: string,
    chargePointKind: ChargePointKind,
    chargePointName: string,
    pricePolicyCurrent: WattsonPricePolicy,
    pricePolicyUpcoming: WattsonPricePolicy,
  ): Observable<ChatResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-rest-endpoint-key': environment.wattsonEndpointKey,
    });

    const payload: CognigyChatRequest = {
      userId,
      sessionId: this.sessionId,
      text: `{env: ${environment.app_identifier}, cp: ${chargePointKind}, cp_name: ${chargePointName}, price_policy_current: ${pricePolicyCurrent}, price_policy_upcoming: ${pricePolicyUpcoming}} ${question}`,
    };

    return this.http
      .post<CognigyResponse>(environment.wattsonEndpointUrl, payload, { headers })
      .pipe(
        map((response) => this.mapCognigyResponse(response)),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  private mapCognigyResponse(response: CognigyResponse): ChatResponse {
    const text = response.text?.trim();
    if (!text) {
      return { answer: FALLBACK_ANSWER };
    }

    const { preamble, payload } = this.extractBotPayload(text);
    if (!payload) {
      return { answer: text };
    }

    const answerParts = [preamble, payload.answer].filter((part) => !!part?.trim());
    return {
      answer: answerParts.join('\n\n') || FALLBACK_ANSWER,
    };
  }

  private extractBotPayload(text: string): {
    preamble: string;
    payload: CognigyBotPayload | null;
  } {
    const jsonStart = text.indexOf('{');
    if (jsonStart === -1) {
      return { preamble: text, payload: null };
    }

    const jsonEnd = text.lastIndexOf('}');
    if (jsonEnd <= jsonStart) {
      return { preamble: text, payload: null };
    }

    const preamble = text.slice(0, jsonStart).trim();
    const jsonCandidate = text.slice(jsonStart, jsonEnd + 1);

    try {
      const parsed = JSON.parse(jsonCandidate) as CognigyBotPayload;
      if (typeof parsed.answer === 'string') {
        return { preamble, payload: parsed };
      }
    } catch {
      // fall through
    }

    return { preamble: text, payload: null };
  }
}
